import { CommonConstants } from "@/common/CommonConstants";
import {
  DriverWorkCycleEdit,
  DriverWorkCycleError,
} from "@/models/driverWorkShiftsAndCycle";
import { DriverWorkShift } from "@/models/operationPlans";
import {
  CYCLE_MAX_ROW,
  CYCLE_MIN_ROW,
  DriverWorkCyclesConstants,
  validDescription,
  validName,
} from "@/pages/DriverWorkCycles/DriverWorkCyclesConstants";
import { uuidv4 } from "@/util";
import { Messages } from "@/util/Messages";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import TextField from "../atoms/TextField";
import { ActionButtons } from "../common/buttonGroup/ActionButtons";
import { Autocomplete } from "../molecules/AutoComplete";
import { Button } from "../molecules/Button";
import Title from "../molecules/Title";
import { TitleGroup } from "../molecules/TitleGroup";

export type DriverWorkCycleEditTemplateType = {
  title:
    | typeof DriverWorkCyclesConstants.DriverWorkCycleEdit
    | typeof DriverWorkCyclesConstants.DriverWorkCycleCreate;
  driverWorkCycle: DriverWorkCycleEdit;
  driverWorkShifts: DriverWorkShift[];
  effectiveCycle?: boolean;
  loading: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  onChangeData: (driverWorkCycle: DriverWorkCycleEdit) => void;
};

export const DriverWorkCycleEditTemplate = ({
  title,
  driverWorkCycle,
  driverWorkShifts,
  effectiveCycle,
  loading,
  onCancel,
  onSubmit,
  onChangeData,
}: DriverWorkCycleEditTemplateType) => {
  const forEdit = useMemo(
    () => title === DriverWorkCyclesConstants.DriverWorkCycleEdit,
    [title]
  );

  const [error, setError] = useState<DriverWorkCycleError>({});
  const [shiftsError, setShiftError] = useState<{ [key: string]: boolean }>({});

  const handleAddShift = useCallback(() => {
    const next = [...(driverWorkCycle?.driverWorkShifts ?? [])];
    next.push({
      key: uuidv4(),
      number: next.length + 1,
      shift: null,
    });

    onChangeData({
      ...driverWorkCycle,
      driverWorkShifts: next,
    });
  }, [driverWorkCycle, onChangeData]);

  const handleDeleteShift = useCallback(() => {
    if ((driverWorkCycle?.driverWorkShifts?.length ?? 0) <= 1) {
      return;
    }

    const current = [...(driverWorkCycle?.driverWorkShifts ?? [])];
    current.pop();

    onChangeData({
      ...driverWorkCycle,
      driverWorkShifts: current,
    });
  }, [driverWorkCycle, onChangeData]);

  const handleCangeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeData({
        ...driverWorkCycle,
        shortName: e.target.value,
      });
      setError({
        ...error,
        shortName: false,
      });
    },
    [driverWorkCycle, error, onChangeData]
  );

  const handleChangeDescription = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeData({
        ...driverWorkCycle,
        description: e.target.value,
      });
      setError({
        ...error,
        description: false,
      });
    },
    [driverWorkCycle, error, onChangeData]
  );

  const handleSelectShift = useCallback(
    (value: DriverWorkShift | null, key: string) => {
      if (driverWorkCycle.driverWorkShifts == null) {
        return;
      }

      const driverWorkShiftsNext = [...driverWorkCycle.driverWorkShifts];
      const target = driverWorkShiftsNext.findIndex((s) => s.key === key);
      if (target === -1) {
        return;
      }

      driverWorkShiftsNext[target].shift = value;
      onChangeData({
        ...driverWorkCycle,
        driverWorkShifts: driverWorkShiftsNext,
      });

      if (key in shiftsError) {
        setShiftError({
          ...shiftsError,
          [key]: false,
        });
      }
    },
    [driverWorkCycle, onChangeData, shiftsError]
  );

  const handleSubmit = useCallback(() => {
    if (!driverWorkCycle.driverWorkShifts?.length) {
      return;
    }

    const currentError = {
      shortName:
        !driverWorkCycle.shortName?.length ||
        !validName(driverWorkCycle.shortName),
      description: !driverWorkCycle.description?.length
        ? false
        : !validDescription(driverWorkCycle.description),
    };

    const currentShiftsError = Object.fromEntries(
      driverWorkCycle.driverWorkShifts.map(({ key, shift }) => [
        key,
        shift == null,
      ])
    );

    if (
      Object.values(currentError).some((error) => error) ||
      Object.values(currentShiftsError).some((error) => error)
    ) {
      setError(currentError);
      setShiftError(currentShiftsError);
      return;
    }

    onSubmit();
  }, [
    driverWorkCycle.description,
    driverWorkCycle.driverWorkShifts,
    driverWorkCycle.shortName,
    onSubmit,
  ]);

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title>{title}</Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <Table
                size="small"
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "0px 4px",
                  backgroundColor: "#FFF",
                  mb: 2,
                  width: "60%",
                }}
                className="gx-info-table-no-width"
              >
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={CommonConstants.CompanyOffice}
                        value={driverWorkCycle.offices?.displayName ?? ""}
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                        }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="required">
                      <TextField
                        label={DriverWorkCyclesConstants.DriverWorkCycleName}
                        value={driverWorkCycle?.shortName ?? ""}
                        onChange={handleCangeName}
                        error={!!error.shortName}
                        errorLabel={
                          !driverWorkCycle?.shortName?.length
                            ? `${DriverWorkCyclesConstants.DriverWorkCycle}${Messages.PleaseEnter}`
                            : DriverWorkCyclesConstants.LengthError
                        }
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <TextField
                        label={DriverWorkCyclesConstants.Description}
                        value={driverWorkCycle.description ?? ""}
                        onChange={handleChangeDescription}
                        error={!!error.description}
                        errorLabel={
                          !driverWorkCycle.description?.length
                            ? `${DriverWorkCyclesConstants.Description}${Messages.PleaseEnter}`
                            : DriverWorkCyclesConstants.DescriptionError
                        }
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Table size="small" sx={{ width: "60%" }}>
                <TableHead className="list-head">
                  <TableRow>
                    <TableCell colSpan={6} className="body-none-custom">
                      <TitleGroup
                        style={{
                          marginBottom: 0,
                          marginTop: 10,
                          fontSize: "1rem",
                        }}
                        text={DriverWorkCyclesConstants.DriverWorkCycle}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ width: "150px" }}>
                      {DriverWorkCyclesConstants.DriverWorkCycleNumber}
                    </TableCell>
                    <TableCell>
                      {DriverWorkCyclesConstants.DriverWorkShifts}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {driverWorkCycle.driverWorkShifts?.map((row, idx, ref) => (
                    <TableRow key={row.key}>
                      <TableCell align="left">{row.number}</TableCell>
                      <TableCell>
                        <Autocomplete<DriverWorkShift>
                          options={driverWorkShifts}
                          value={row.shift}
                          getOptionLabel={(opt) => opt?.shortName ?? ""}
                          isOptionEqualToValue={(opt, val) => opt.id === val.id}
                          onChange={(e, val) => handleSelectShift(val, row.key)}
                          error={!!shiftsError[row.key]}
                          errorLabel={`${DriverWorkCyclesConstants.DriverWorkShifts}${Messages.PleaseEnter}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell className="body-none-custom" />
                    <TableCell align="right" className="body-none-custom">
                      <Button
                        sx={{ backgroundColor: (theme) => theme.palette.primary.main }}
                        onClick={handleAddShift}
                        disabled={
                          effectiveCycle ||
                          (driverWorkCycle.driverWorkShifts?.length ??
                            CYCLE_MAX_ROW) >= CYCLE_MAX_ROW
                        }
                        label={DriverWorkCyclesConstants.DriverWorkCycleAdd}
                      />
                      <Button
                        sx={{ backgroundColor: "#E71A19", ml: 2 }}
                        onClick={handleDeleteShift}
                        disabled={
                          effectiveCycle ||
                          (driverWorkCycle.driverWorkShifts?.length ??
                            CYCLE_MIN_ROW) <= CYCLE_MIN_ROW
                        }
                        label={DriverWorkCyclesConstants.DriverWorkCycleRemov}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <ActionButtons
                loading={loading}
                forSave={!forEdit}
                onCancel={onCancel}
                onSubmit={handleSubmit}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
